import { useRef } from 'react';
import { motion } from 'framer-motion';
import { Box, Button, Container, Stack } from '@mui/material';
import { Download, Link, Share2 } from 'lucide-react';
import ResultsCard from './ResultsCard';
import { useShareResults } from '../hooks/useShareResults';
import { useAchievements } from '../../../hooks/useAchievements';
import { groupAchievementsForPeriod } from '../../achievements/achievementHelpers';

interface Answer {
  questionId: string | null;
  questionText: string;
  userAnswer: string;
  isCorrect: boolean;
  correctAnswer: string;
  submittedAt: string;
}

interface QuizResultsCardProps {
  score: number;
  totalQuestions: number;
  modeName: string;
  answers: Answer[];
  user: { userId?: string; username?: string; team?: string } | null;
  onPlayAgain: () => void;
}

export default function QuizResultsCard({
  score,
  totalQuestions,
  modeName,
  answers,
  user,
  onPlayAgain,
}: QuizResultsCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const team = user?.team ?? null;

  const {
    handleNativeShare,
    handleCopyLink,
    handleDownloadImage,
    copyState,
    downloadState,
    canNativeShare,
  } = useShareResults({ score, totalQuestions, modeName, team, cardRef });

  // Check whether the user currently holds any leaderboard achievement (any period).
  // If so, the leaderboard button gets a pulsing golden glow to draw attention.
  const { data: achievements } = useAchievements(user?.userId ?? '');
  const isOnLeaderboard = groupAchievementsForPeriod(achievements ?? [], 'alltime').length > 0;

  return (
    <Container maxWidth="sm">
      <Box pt={4} pb={2} display="flex" flexDirection="column" alignItems="center" gap={3}>

        {/* The shareable card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          style={{ width: '100%' }}
        >
          <Box ref={cardRef} sx={{ width: '100%' }}>
            <ResultsCard
              score={score}
              totalQuestions={totalQuestions}
              modeName={modeName}
              team={team}
            />
          </Box>
        </motion.div>

        {/* Action buttons */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
          style={{ width: '100%', maxWidth: 420 }}
        >
          <Stack direction="row" spacing={1} rowGap={1} justifyContent="center" flexWrap="wrap">
            {canNativeShare && (
              <Button
                onClick={handleNativeShare}
                startIcon={<Share2 size={15} />}
                variant="contained"
                sx={{
                  background: '#534AB7',
                  color: '#e0c9f0',
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  px: 2.5,
                  '&:hover': { background: '#6b62c7' },
                }}
              >
                Share results
              </Button>
            )}

            <Button
              onClick={handleCopyLink}
              startIcon={<Link size={15} />}
              variant="outlined"
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                fontSize: '0.85rem',
                px: 2.5,
                borderColor: 'rgba(127,119,221,0.4)',
                color: '#AFA9EC',
                '&:hover': { borderColor: '#7F77DD', background: 'rgba(83,74,183,0.1)' },
              }}
            >
              {copyState === 'copied' ? 'Copied!' : 'Copy link'}
            </Button>

            <Button
              onClick={handleDownloadImage}
              startIcon={<Download size={15} />}
              variant="outlined"
              disabled={downloadState === 'loading'}
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                fontSize: '0.85rem',
                px: 2.5,
                borderColor: 'rgba(127,119,221,0.4)',
                color: '#AFA9EC',
                '&:hover': { borderColor: '#7F77DD', background: 'rgba(83,74,183,0.1)' },
              }}
            >
              {downloadState === 'loading' ? 'Saving...' : downloadState === 'done' ? 'Saved!' : 'Save image'}
            </Button>
          </Stack>

          <Box mt={2} display="flex" justifyContent="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                onClick={onPlayAgain}
                sx={{
                  textTransform: 'none',
                  color: 'text.secondary',
                  fontSize: '0.85rem',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                Play again
              </Button>

              {/* Leaderboard button — promoted to outlined + golden pulse when user is on a leaderboard */}
              <Button
                href="/leaderboard"
                variant={isOnLeaderboard ? 'outlined' : 'text'}
                sx={isOnLeaderboard ? {
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  borderColor: 'rgba(255,215,0,0.6)',
                  color: '#ffd700',
                  animation: 'leaderboardPulse 2.5s ease-in-out infinite',
                  '@keyframes leaderboardPulse': {
                    '0%, 100%': {
                      boxShadow: '0 0 0px 0px rgba(255,215,0,0)',
                      borderColor: 'rgba(255,215,0,0.4)',
                    },
                    '50%': {
                      boxShadow: '0 0 10px 2px rgba(255,215,0,0.3)',
                      borderColor: 'rgba(255,215,0,0.85)',
                    },
                  },
                  '&:hover': {
                    borderColor: '#ffd700',
                    background: 'rgba(255,215,0,0.08)',
                  },
                } : {
                  textTransform: 'none',
                  color: 'text.secondary',
                  fontSize: '0.85rem',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                Leaderboard
              </Button>
            </Stack>
          </Box>
        </motion.div>

      </Box>
    </Container>
  );
}